import { AxiosResponse } from 'axios';
import React, { useRef, useState } from 'react';

function parseFileName(contentHeader: string) {
  const kvp = contentHeader.split(';').map((x) => x.trim());
  const fileName = kvp.find((x) => x.includes('filename'))?.split('=');

  return fileName ? decodeURIComponent(fileName[1]).replaceAll('"', '') : 'file.csv';
}

function useReportDownload(
  // eslint-disable-next-line @typescript-eslint/no-shadow
  getData: (...args: any[]) => Promise<AxiosResponse<any>>,
  ...args: any[]
) {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState('');
  const [fileName, setFileName] = useState('');

  // The most basic way to download a file within JS is to create a blob from the data,
  // then use an anchor tag to trigger the download
  const downloadReport = async () => {
    try {
      setLoading(true);
      const { data, headers } = await getData(...args);
      const dataUrl = window.URL.createObjectURL(new Blob([data]));
      setFileName(parseFileName(headers['content-disposition'] || ''));
      setUrl(dataUrl);
      ref?.current?.click();

      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  return {
    downloadReport,
    url,
    fileName,
    ref,
    loading,
  };
}

interface Props {
  getData: (...args: any[]) => Promise<AxiosResponse<any>>;
  children: (args: ChildProps) => React.ReactNode;
  args: any[];
}

interface ChildProps {
  onClick: () => void;
  loading: boolean;
}

const FileDownload = ({ getData, args, children }: Props) => {
  const { downloadReport, url, fileName, ref, loading } = useReportDownload(getData, ...args);

  return (
    <>
      <a
        href={url}
        ref={ref}
        hidden={true}
        download={fileName}
        className="hidden"
        aria-hidden="true">
        download
      </a>
      {children({ onClick: downloadReport, loading })}
    </>
  );
};

export default FileDownload;
